import { AxiosError, AxiosResponse } from 'axios'
import { toast } from 'react-toastify'
import { t } from 'i18next'
import { mapEntityToTranslatedString, extractErrorMessageFromBadRequestReason } from '../helpers/errors.helper'
import { BadRequestResponse } from '../types/Errors'
import i18n from '../i18n'
import { ErrorResponse, parseAxiosTransitErrorResponse } from '../shared/api/parseAxiosTransitErrorResponse'

const baseUrl = process.env.REACT_APP_API_URL
const apiVersion = process.env.REACT_APP_API_VERSION

function getApiUrl() {
  return `${baseUrl}/v${apiVersion}`
}

export function getBaseApiUrl(path?: string) {
  return `${baseUrl}${path ?? ''}`
}

export function getAuthHeader(token: string) {
  return {
    Authorization: process.env.REACT_APP_API_AUTHORIZATION_PREFIX + token,
  }
}

function getFullApiUrl(additionalPath: string): string {
  return getApiUrl() + additionalPath
}

export function handleResponseData(response: AxiosResponse) {
  if (response.status === 200 || response.status === 201) {
    return Promise.resolve(response.data.data)
  }
  if (response.status >= 200 && response.status < 300) {
    return Promise.resolve(response.data)
  }

  if ([401, 403].indexOf(response.status) !== -1) {
    // automatic logout when Unauthorized or Forbidden response is returned from api
    // eslint-disable-next-line no-console
    console.error(response.status, response.statusText)
  }

  // eslint-disable-next-line no-console
  console.error(`Response returned error ${response.status}`, response.statusText)
  return Promise.reject(Error(`Response returned error ${response.status} >> ${response.statusText}`))
}

export function handleApiException(error: AxiosError<ErrorResponse>) {
  if ([401, 403].indexOf(error.request.status) !== -1) {
    // automatic logout when Unauthorized or Forbidden response is returned from api
  }

  const {
    traceId,
    isConstraintViolation,
    constraintViolations,
    isNotFoundError,
    notFoundError,
  } = parseAxiosTransitErrorResponse(error)

  if (isNotFoundError && notFoundError) {
    const translatedString = mapEntityToTranslatedString(notFoundError)

    if (translatedString !== '') {
      // @ts-ignore
      toast.error(t('messages.notFoundError', {
        entity: translatedString,
        field: notFoundError.criteria[0].field.toUpperCase(),
        value: notFoundError.criteria[0].value,
      }))
    } else if (notFoundError.entity !== 'DirectoEvent') {
      toast.error(
        `Not Found! Please send this ID: ${traceId} to support!`,
        { autoClose: false, closeOnClick: false, draggable: false },
      )
    }
  }

  if (isConstraintViolation && constraintViolations.length) {
    constraintViolations.forEach((errorItem: BadRequestResponse) => {
      const translatedString = extractErrorMessageFromBadRequestReason(errorItem)
      if (translatedString !== '' && translatedString !== null && translatedString !== undefined) {
        toast.error(`${t(`translations${i18n.language.toUpperCase()}:${errorItem.field}`)} ${translatedString}`)
      } else if (error.request.field === null) {
        toast.error(
          `Bad Request! Please send this ID: ${traceId} to support!`,
          { autoClose: false, closeOnClick: false, draggable: false },
        )
      }
    })
  }

  const message = (error.request.response ? JSON.parse(error.request.response).message : null)
  if (error.request.status === 409 && message) {
    toast.error(
      `Bad Request! ${message}`,
      { autoClose: false, closeOnClick: false, draggable: false },
    )
  }

  if (error.request.status === 400 && message) {
    toast.error(
      message,
      { autoClose: false, closeOnClick: false, draggable: false },
    )
  }

  if (error.request.status === 500) {
    toast.error(
      `Please contact support and send this ID: ${error.request.data.id}`,
      { autoClose: false, closeOnClick: false, draggable: false },
    )
  }
  // eslint-disable-next-line no-console
  console.error(`Response error code ${error.request.status}`, error.request.statusText)
  return Promise.reject(error.request.data || `Response returned error ${error.request.status} >> ${error.request.statusText}`)
}

export const apiService = {
  getApiUrl,
  getFullApiUrl,
}
